import * as R from 'ramda'

const SENTIMENT_SORT_RANK = { positive: 0, neutral: 1, negative: 2 }

function isFlippableQuestion(q) {
  if (!q) {
    return false
  }
  // CES is a `rating_scale_minmax` question, not a `multipleChoice` — accept it
  // explicitly so a CES-only survey is flippable end-to-end (PRD Q2 Option A).
  const isMultipleChoice = q.type === 'multipleChoice'
  const isCES = q.type === 'rating_scale_minmax' && q.metric === 'CES'
  if (!isMultipleChoice && !isCES) {
    return false
  }
  if (q.metric === 'NPS') {
    return false
  }
  if (q.metric === 'CSAT' || q.metric === 'CES') {
    return true
  }
  if (q.isOpinionScale || q.is_opinion_scale) {
    return true
  }
  return Boolean(q.choices && q.choices.some((c) => c && c.sentiment))
}

function getFollowUpQuestions(q) {
  const questionAction = q.questionAction || q.question_action
  const conditions = (questionAction && questionAction.conditions) || []
  return conditions.flatMap((c) =>
    [
      c.followUpMultipleChoiceQuestion,
      c.follow_up_multiple_choice_question,
      c.followUpCommentQuestion,
      c.follow_up_comment_question,
    ].filter(Boolean)
  )
}

export const surveyHasFlippableQuestion = (questions) => {
  if (R.isNil(questions) || R.isEmpty(questions)) {
    return false
  }
  return questions.some((q) => {
    if (isFlippableQuestion(q)) {
      return true
    }
    return getFollowUpQuestions(q).some(isFlippableQuestion)
  })
}

export const sortChoicesPositiveFirst = (choices, isReverseOrder = false) => {
  const hasSentiment = choices.some((choice) => choice.sentiment)
  if (!hasSentiment) {
    return [...choices]
  }
  const sorted = [...choices].sort((a, b) => {
    const rankDiff =
      R.propOr(1, a.sentiment, SENTIMENT_SORT_RANK) - R.propOr(1, b.sentiment, SENTIMENT_SORT_RANK)
    if (rankDiff !== 0) {
      return rankDiff
    }
    return Number(b.value) - Number(a.value)
  })
  return isReverseOrder ? sorted.reverse() : sorted
}

/**
 * Question-aware canonical sort for PET-1967 flip-choice-order rendering.
 * Mirrors web-survey's `organisms/ChoicesWithImage/utils.js#sortChoicesForDisplay`.
 *
 * @param {object} question — must have `metric` + `choices`
 * @param {boolean} isReverseOrder — the survey's `settings.setups.choiceOrder`
 * @returns {array} choices in canonical positive-first order, reversed to
 *   negative-first when `isReverseOrder` is true. Non-sentiment non-CES
 *   questions get the raw builder order back (WYSIWYG per PRD Q3 Option A).
 */
export const sortChoicesForDisplay = (question, isReverseOrder = false) => {
  const choices = question.choices || []
  const hasSentiment = choices.some((choice) => choice.sentiment)
  if (!hasSentiment && question.metric !== 'CES') {
    return [...choices]
  }
  const sorted = [...choices].sort((a, b) => {
    const rankDiff =
      R.propOr(1, a.sentiment, SENTIMENT_SORT_RANK) - R.propOr(1, b.sentiment, SENTIMENT_SORT_RANK)
    if (rankDiff !== 0) {
      return rankDiff
    }
    return Number(b.value) - Number(a.value)
  })
  return isReverseOrder ? sorted.reverse() : sorted
}

export const sortConditionsBySentiment = (conditions, isReverseOrder = false) => {
  const sentimentRank = (condition) => {
    const ranks = (condition.rules || [])
      .filter((rule) => rule in SENTIMENT_SORT_RANK)
      .map((rule) => SENTIMENT_SORT_RANK[rule])
    if (R.isEmpty(ranks)) {
      return null
    }
    const best = Math.min(...ranks)
    return isReverseOrder ? 2 - best : best
  }
  const withSentiment = conditions.filter((condition) => sentimentRank(condition) !== null)
  const withoutSentiment = conditions.filter((condition) => sentimentRank(condition) === null)
  const sorted = [...withSentiment].sort((a, b) => sentimentRank(a) - sentimentRank(b))
  return [...sorted, ...withoutSentiment]
}

export const isNPSPrimaryQuestion = (questions) => {
  const notNilAndNotEmpty = !R.isEmpty(questions) && !R.isNil(questions)
  return notNilAndNotEmpty && R.head(questions).metric === 'NPS'
}

export const getSurveyMetricLabel = (questions) => {
  if (!R.isEmpty(questions) && !R.isNil(questions)) {
    const metric = R.head(questions).metric
    const labels = {
      NPS: npsInsightWidgetLabel,
      CES: cesInsightWidgetLabel,
      CSAT: csatInsightWidgetLabel,
    }
    return labels[metric]
  } else {
    return csatInsightWidgetLabel
  }
}

const npsInsightWidgetLabel = {
  name: 'Net Promoter Score',
  positive: 'Promoters',
  neutral: 'Passives',
  negative: 'Detractors',
}

const cesInsightWidgetLabel = {
  name: 'Customer Effort Score',
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative',
}

const csatInsightWidgetLabel = {
  name: 'Satisfaction',
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative',
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/libs/questions/index.js